import { useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Upload } from 'lucide-react';
import { toast } from '../studioToast';
import { API_URL } from '../../../utils/api';
import { getErrorMessage } from '../../../utils/errors';
import { cn } from '../../../lib/utils';
import { assetUrl } from '../../design/DesignLayers';
import { EYEBROW, FOCUS_RING } from '../constants';
import { PanelHeading } from '../controls';

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 25 * 1024 * 1024;

/** Uploads tool: upload photos and click one to place it on the page. */
export const UploadsPanel = ({ photos, onUploaded, onPlace }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error('Please choose a JPG, PNG, WebP or GIF image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('That photo is larger than 25 MB.');
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/upload/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data?.url) throw new Error('No url');
      onUploaded(data.url);
      onPlace(data.url);
      toast.success('Photo added');
    } catch (error) {
      toast.error(getErrorMessage(error, 'We could not upload that photo.'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      <PanelHeading>Uploads</PanelHeading>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(e.dataTransfer.files?.[0]);
        }}
        disabled={uploading}
        className={cn(
          'h-[150px] rounded-2xl border-2 border-dashed flex flex-col gap-2 items-center justify-center text-center px-5',
          dragOver ? 'border-pink bg-[#FBEAF4]' : 'border-[#D8B6CC] bg-[#FCF7FA] hover:bg-[#FAF0F6]',
          FOCUS_RING
        )}
        data-testid="upload-photo-btn"
      >
        {uploading ? <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" /> : <Upload className="h-7 w-7" aria-hidden="true" />}
        <span className="text-sm font-extrabold text-ink">{uploading ? 'Uploading…' : 'Upload a photo'}</span>
        <span className="text-xs text-[#6B665C]">JPG or PNG, up to 25 MB. The bar mitzvah boy, the couple, the family.</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED.join(',')}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="upload-input"
      />

      <p className={EYEBROW}>Your photos</p>
      {photos.length === 0 ? (
        <p className="text-sm text-[#6B665C]">Photos you upload appear here.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {photos.map((src) => (
            <button
              key={src}
              type="button"
              onClick={() => onPlace(src)}
              aria-label="Place this photo on the invitation"
              className={cn('aspect-square rounded-xl overflow-hidden bg-cream hover:opacity-90', FOCUS_RING)}
            >
              <img src={assetUrl(src)} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      <p className="text-xs leading-relaxed text-[#6B665C]">
        Click a photo to place it. Round corners, fit and transparency are in the panel on the right.
      </p>
    </div>
  );
};

export default UploadsPanel;
